import {
    afterEveryRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    signal
} from '@angular/core';
import { httpResource } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { DepartmentService } from 'src/app/services/department.service';
import { MonitoringScopeDto } from '../queue-request-browser/queue-request.model';
import { QueueRequestService } from '../queue-request-browser/queue-request.service';
import { QueueRequestBrowserComponent } from '../queue-request-browser/queue-request-browser.component';

/** Breathing room below the card, so it stops short of the viewport edge. */
const CARD_BOTTOM_GAP = 16;

/** Below this the card is unusable, and letting the page scroll is the better trade. */
const CARD_MIN_HEIGHT = 360;

/**
 * Overvågning — department-scoped view of synchronization work in flight.
 *
 * A thin shell around <app-queue-request-browser>: it owns the page chrome, the measured card
 * height and the two scope dropdowns, and feeds the browser its customer/synchronization inputs.
 * Everything below the header — filters, list, detail accordion, polling — belongs to the browser,
 * which the synchronization detail page embeds too. Picking "sync A" here is therefore the same
 * view as opening sync A directly, by construction rather than by convention.
 */
@Component({
    selector: 'app-synchronization-monitoring',
    templateUrl: './synchronization-monitoring.component.html',
    styleUrl: './synchronization-monitoring.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [QueueRequestBrowserComponent]
})
export class SynchronizationMonitoringComponent {
    private readonly departmentService = inject(DepartmentService);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly api = inject(QueueRequestService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    /**
     * Measured, not a `calc(100vh - …)` guess. The offset above the card is not a constant: this
     * page sits under a sub-menu bar that carries tabs, so it starts lower than the detail page
     * does, and the error banner can push it down further still. Guessing meant the card overflowed
     * and the browser grew a scrollbar around it.
     */
    readonly cardHeight = signal<number | null>(null);

    constructor() {
        // Two triggers, because neither covers the other:
        //  - `resize` is the only signal for a viewport change. A ResizeObserver cannot substitute:
        //    while the card overflows, body height is content-driven, so it does not change with the
        //    viewport and the observer never fires — which is exactly how an over-tall card stayed
        //    over-tall.
        //  - the render hook catches the offset above the card moving at an unchanged viewport, the
        //    error banner being the case that does it. Preferred over a ResizeObserver on the content
        //    column: zone.js does not patch ResizeObserver, so its callback lands outside the Angular
        //    zone and the signal write never schedules a tick — the height ends up right in the
        //    signal and stale in the DOM.
        window.addEventListener('resize', this.measure);
        this.destroyRef.onDestroy(() => window.removeEventListener('resize', this.measure));
        afterEveryRender({ read: this.measure });
    }

    /**
     * Arrow property so the same reference registers and unregisters as an event listener. Cheap
     * enough to run after every render: one layout read, and the epsilon guard below stops a write
     * from provoking the next render into writing again.
     */
    private readonly measure = (): void => {
        const card = this.host.nativeElement.querySelector<HTMLElement>('.monitoring-card');
        if (!card) return;

        // Document-relative top, so the result does not depend on the current scroll. It never
        // depends on our own height either — nothing above the card is sized by the card.
        const documentTop = card.getBoundingClientRect().top + window.scrollY;
        const available = window.innerHeight - documentTop - CARD_BOTTOM_GAP;
        const next = Math.max(CARD_MIN_HEIGHT, Math.round(available));
        if (Math.abs((this.cardHeight() ?? 0) - next) > 1) this.cardHeight.set(next);
    };

    private readonly departmentId = computed(() => this.departmentService.selectedDepartment()?.id ?? null);

    readonly departmentName = computed(() =>
        this.departmentService.selectedDepartment()?.name ?? 'afdelingen'
    );

    /**
     * The same payload the browser loads for its name lookups. Two subscribers, one HTTP call — the
     * Angular HTTP layer does not dedupe, but both resources key on the same URL and the browser
     * caches nothing, so the cost is one extra GET per department change. Cheaper than threading the
     * scope down as an input and giving the browser two ways to get its names.
     */
    private readonly scopeResource = httpResource<MonitoringScopeDto>(() => {
        const departmentId = this.departmentId();
        if (!departmentId) return undefined;
        return { url: this.api.scopeUrl(departmentId, null), method: 'GET', ...this.api.requestOptions };
    });

    readonly customers = computed(() => this.scopeResource.value()?.customers ?? []);

    private readonly initialParams = this.route.snapshot.queryParams;

    readonly customerFilter = signal<number | null>(
        this.initialParams['customerId'] ? Number(this.initialParams['customerId']) : null
    );
    readonly synchronizationFilter = signal<string | null>(this.initialParams['syncId'] ?? null);

    /** The synchronization select narrows with the customer select — that is the whole point of /scope. */
    readonly availableSynchronizations = computed(() => {
        const customerId = this.customerFilter();
        return this.customers()
            .filter(customer => customerId === null || customer.customerId === customerId)
            .flatMap(customer =>
                customer.synchronizations.map(sync => ({
                    id: sync.id,
                    label: customerId === null ? `${customer.customerName} — ${sync.name}` : sync.name
                }))
            );
    });

    readonly hasPageFilter = computed(() =>
        this.customerFilter() !== null || this.synchronizationFilter() !== null
    );

    onCustomerFilterChange(value: string): void {
        this.customerFilter.set(value ? Number(value) : null);
        // A synchronization from another customer would contradict the customer just chosen.
        const stillVisible = this.availableSynchronizations().some(s => s.id === this.synchronizationFilter());
        if (!stillVisible) this.synchronizationFilter.set(null);
        this.writeScopeParams();
    }

    onSynchronizationFilterChange(value: string): void {
        this.synchronizationFilter.set(value || null);
        this.writeScopeParams();
    }

    clearPageFilters(): void {
        this.customerFilter.set(null);
        this.synchronizationFilter.set(null);
        this.writeScopeParams();
    }

    /**
     * Same idiom the browser uses for its own params, and `merge` is what keeps the two writers from
     * clobbering each other's keys.
     */
    private writeScopeParams(): void {
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                customerId: this.customerFilter(),
                syncId: this.synchronizationFilter()
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }
}
